import { Slider } from '@massilia/ui'

import { formatPrice } from '@/utils/format-price'

import { FilterDrawer } from './filter-drawer'
import { boundsToRange } from './filter-url-params'
import { useFiltersState } from './filters-state-context'

export interface PriceRange {
  max: number
  min: number
}

interface PriceFilterProps {
  bounds: PriceRange
}

export function PriceFilter({ bounds }: PriceFilterProps) {
  const {
    filters: { filterPriceMax, filterPriceMin },
    setFilters,
  } = useFiltersState()
  // Même règle que les pages de résultats (`boundsToRange`) : une borne SEULE
  // est une contrainte valide. Le chat ne pose que des plafonds ; les ignorer
  // ici laissait le curseur immobile sur toute sa plage alors que la liste,
  // elle, était bien filtrée — l'utilisateur voyait un filtre sans le voir.
  // Les bornes ouvertes (0 / +∞) sont ramenées dans la plage réelle par le
  // clamp ci-dessous, ce qui évite aussi de replier le curseur sur [min, min].
  const range = boundsToRange(filterPriceMin, filterPriceMax)
  const currentMin = Math.max(range?.min ?? bounds.min, bounds.min)
  const currentMax = Math.min(range?.max ?? bounds.max, bounds.max)
  // If the chatbot (or a stale URL) provides a ceiling below the live floor,
  // currentMin would end up greater than currentMax and Radix Slider would
  // collapse both thumbs onto the same point. Keep them distinguishable.
  const safeMax = Math.max(currentMin, currentMax)

  const handleValueChange = (values: number[]) => {
    setFilters({ filterPriceMax: values[1], filterPriceMin: values[0] })
  }

  return (
    <FilterDrawer title="Prix">
      <div className="space-y-4">
        {/* Price display */}
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">{formatPrice(currentMin)}</span>
          <span className="text-gray-400">—</span>
          <span className="font-medium text-gray-700">{formatPrice(safeMax)}</span>
        </div>

        {/* Slider */}
        <Slider
          max={bounds.max}
          min={bounds.min}
          onValueChange={handleValueChange}
          step={10}
          value={[currentMin, safeMax]}
        />

        {/* Bounds display */}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Min: {formatPrice(bounds.min)}</span>
          <span>Max: {formatPrice(bounds.max)}</span>
        </div>
      </div>
    </FilterDrawer>
  )
}
